// Angular Files
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  OnInit,
  ViewChild,
  Inject,
  Optional,
  AfterViewInit,
  DOCUMENT
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

// Angular Material Files
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';

// Other External Files
import { cloneDeep } from 'lodash-es';

// Teller Online Files
import { ItemDto, ItemPaymentOptionDto } from 'apps/public-portal/src/app/core/api/PublicPortalApiClients';
import { currencyMaskOutput } from 'apps/public-portal/src/app/core/utils/currency-mask.utils';

// Teller Online Library Files
import { TellerOnlineValidationService } from 'teller-online-libraries/shared';
import { TellerOnlineAppService } from 'teller-online-libraries/core';

/** What the narrow-viewport bottom sheet is opened with. The sheet is created imperatively, so
 * nothing the panel takes as an @Input can be bound — it all has to arrive as sheet data, and a
 * missing field fails silently rather than at the template. */
export interface SelectPaymentSheetData {
    item: ItemDto;
    conflictingOptions: SelectedPaymentOption[];
}

@Component({
    selector: 'app-select-payment',
    templateUrl: './select-payment.component.html',
    styleUrls: ['./select-payment.component.scss'],
    host: {
        class: 'select-payment'
    },
    standalone: false
})
export class SelectPaymentComponent implements OnInit, AfterViewInit {
    @Input() item: ItemDto;

    /** A list of options that are causing a conflict in the dropdown and will be displayed with a red border */
    @Input() conflictingOptions: SelectedPaymentOption[];

    @Output() closePopup = new EventEmitter<boolean>();

    @Output() updateCartItem = new EventEmitter<any>();

    @Output() addToCart = new EventEmitter<any>();

    @ViewChild('customAmountInput') customAmountInput: ElementRef;

    /** The amount to be saved to a custom payment */
    public customAmount: string = '';

    /** Toggle to indicate if the Custom Amount field should be shown when not selected */
    public showCustomAmountField: boolean = false;

    /** Keeps the two-decimal currency mask binding a number (B32). */
    public currencyMaskOutput = currencyMaskOutput;

    public isBottomSheet: boolean;

    public paymentOptions: SelectedPaymentOption[];

    public customAmountForm: UntypedFormGroup = new UntypedFormGroup({
        Amount: new UntypedFormControl(0)
    });

    constructor(
        @Optional() private matBottomSheetRef: MatBottomSheetRef,
        public validationService: TellerOnlineValidationService,
        public appService: TellerOnlineAppService,
        @Inject(DOCUMENT) private document: Document,
        @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) public data: SelectPaymentSheetData
    ) {
        // A real MatBottomSheetRef is only injected when opened as a bottom sheet; in the panel
        // context these arrive via @Inputs instead.
        if (this.matBottomSheetRef) {
            this.item = this.data?.item;
            this.conflictingOptions = this.data?.conflictingOptions;
        }
    }

    ngOnInit() {
        this.isBottomSheet = !!this.matBottomSheetRef;
        this.paymentOptions = cloneDeep(this.item?.paymentOptions);
        const selectedOption = this.paymentOptions?.find(i => i.name === this.item.selectedPaymentOption?.name && i.amount === this.item.selectedPaymentOption?.amount);
        if (selectedOption) selectedOption.selected = true;

        let customOption;

        if (!this.conflictingOptions?.length) {
            customOption = this.paymentOptions?.find(i => i.isCustomAmount);
            this.customAmount = customOption?.amount?.toFixed(2) ?? '';
        } else {
            selectedOption.selected = false;
            customOption = this.paymentOptions.find(i => i.isCustomAmount);
            // Update the custom amount label to indicate it's a new option
            if (customOption) customOption.label = `New ${customOption.label}`;

            this.conflictingOptions.forEach(option => {
                const paymentOption = cloneDeep(option);
                // If this isn't a custom amount, find it in the existing list and set it's error to true
                if (!paymentOption.isCustomAmount) {
                    this.paymentOptions.find(i => i.name === paymentOption.name).error = true;
                // If this is a custom amount, set it's error to true, and add to the list with a new label
                } else {
                    paymentOption.error = true;
                    paymentOption.label = `Previous ${paymentOption.label}`;
                    this.paymentOptions.unshift(paymentOption);
                }
            });
        }

        // if we have a custom option, add min/max validation on it
        // because we can't use regular forms since we don't want it to be type=number
        if (customOption) {
            const control = this.customAmountForm.controls.Amount;
            control.addValidators([
                Validators.max(customOption.maxAmount),
                Validators.min(customOption.minAmount)
            ]);
            control.setValue(this.customAmount);
        }
    }

    // Focus restoration is owned by the hosts, not here: the wide panel's cdkTrapFocus and the
    // narrow bottom sheet each return focus to the trigger on teardown, and when an option is
    // picked the search page moves it to the "Remove From Cart" button that replaces the
    // trigger. A restore from this component's destroy ran after that and, with the trigger
    // gone, fell through to the page's main-content anchor.
    ngAfterViewInit() {
        this._focusCustomAmount();
    }

    /** When the element receives focus, auto select all the text inside the box for edit */
    onFocusIn_selectText() {
        setTimeout(() => {
            this.customAmountInput.nativeElement.select();
        }, 1);
    }

    /** params are: item, paymentOption, customAmount */
    onClick_updateCartItem(item, paymentOption: SelectedPaymentOption, customAmount?: number) {
        // Remove all the custom amount error'd options, if there were any
        this.paymentOptions = this.paymentOptions?.filter(i => !(i.error && i.isCustomAmount));

        // If this is a previous option and it's custom, copy the amount to the real custom amount option
        if (paymentOption?.error && paymentOption?.isCustomAmount) {
            const customAmountOption = this.paymentOptions?.find(i => i.isCustomAmount);
            customAmount = paymentOption.amount;
            customAmountOption.amount = customAmount;
            // Update the label for the custom payment option
            customAmountOption.label = customAmountOption.label.replace('New ', '');
            // Update the option we're saving to be the custom one
            paymentOption = customAmountOption;
        }

        this.updateCartItem.emit([item, paymentOption, customAmount]);
        this.closePopup.emit();
        this.matBottomSheetRef?.dismiss({
 item, paymentOption, customAmount, action: 'update'
});
    }

    /** params are: item, paymentOption, customAmount */
    onClick_addToCart(...args) {
        this.addToCart.emit(args);
        this.closePopup.emit();
        this.matBottomSheetRef?.dismiss({
 item: args[0], paymentOption: args[1], customAmount: args[2], action: 'add'
});
    }

    onClick_showCustomAmount(e) {
        e.stopPropagation();
        this.showCustomAmountField = true;
        this._focusCustomAmount();
    }

    onSubmit_customPaymentAmount() {
        if (this.validationService.runValidation(this.customAmountForm)) {
            this.customAmount = this.customAmountForm.controls.Amount.value;

            const paymentOption = this.paymentOptions?.find(i => i.isCustomAmount && !i.error);
            if (!this.item.amountInCart) {
                this.onClick_addToCart(this.item, paymentOption, Number(this.customAmount));
            } else {
                this.onClick_updateCartItem(this.item, paymentOption, Number(this.customAmount));
            }
            this.closePopup.emit();
        }
    }

    private _focusCustomAmount() {
        /* For some reason the field doesn't exist yet when this is fired so calling .focus() on it does nothing
        so we'll wait 1 ms so it'll exist and then we'll fire it
        */
        setTimeout(() => {
            if (this.customAmountInput) {
                this.customAmountInput.nativeElement.focus();
            }
        }, 1);
    }
}

export class SelectedPaymentOption extends ItemPaymentOptionDto {
    error?: boolean;

    selected?: boolean;
}
